import { useMemo, useState } from "react";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";

/*
 * QuickAdd — dashboard "create anything" panel.
 *
 * Renders a grid of role-aware action cards. Each card opens a focused modal
 * with the minimum fields needed to create the entity (no image upload,
 * no advanced options — if you need more, go to the full page). Shares the
 * same `modal-overlay` / `modal-container` / `modal-header-wrapper` chrome
 * already defined in Dashboard.css so it feels native.
 *
 * Props:
 *   locations, warehouses, rooms — already fetched by the Dashboard; reused
 *   here so the warehouse / partition dropdowns render without another round
 *   trip.
 *   onCreated — invoked after any successful create; the Dashboard uses it
 *   to refresh all of its lists and stat counters.
 *   onOpenTagModal — opens the existing tag-create modal in Dashboard so we
 *   don't duplicate its rich validation and antenna-lookup logic.
 */

const USER_ROLE_OPTIONS = [
  { value: "admin",    label: "Admin" },
  { value: "manager",  label: "Manager" },
  { value: "worker",   label: "Worker" },
  { value: "operator", label: "Operator" },
  { value: "cashier",  label: "Cashier" },
  { value: "viewer",   label: "Viewer" },
];

const EMPTY_FORMS = {
  location:  { name: "", address: "" },
  warehouse: { name: "", locationId: "" },
  partition: { roomTitle: "", warehouseId: "" },
  product:   { name: "", epc: "", price: "" },
  user:      { username: "", password: "", role: "worker" },
};

const QuickAddCard = ({ icon, label, description, accent, onClick }) => (
  <button type="button" className="quick-action-card" onClick={onClick}>
    <div className="quick-action-icon" style={{ backgroundColor: `${accent}1A`, color: accent }}>
      <i className={`fa ${icon}`} aria-hidden />
    </div>
    <div className="quick-action-text">
      <span className="quick-action-label">{label}</span>
      <span className="quick-action-description">{description}</span>
    </div>
    <i className="fa fa-plus quick-action-arrow" aria-hidden />
  </button>
);

const QuickAdd = ({ locations = [], warehouses = [], onCreated, onOpenTagModal }) => {
  const { showToast } = useToast();
  const {
    isAdmin,
    isManager,
    isWorker,
    isLocationManager,
    canCreateLocation,
    canCreateWarehouse,
    canCreateRoom,
    canCreateUser,
    canManageTags,
  } = useAuth();

  // `openModal` controls which modal is rendered. null = panel only.
  const [openModal, setOpenModal] = useState(null);
  const [forms, setForms] = useState(EMPTY_FORMS);
  const [saving, setSaving] = useState(false);

  const resetAndClose = () => {
    setForms(EMPTY_FORMS);
    setOpenModal(null);
  };

  const updateForm = (entity, patch) => {
    setForms((prev) => ({ ...prev, [entity]: { ...prev[entity], ...patch } }));
  };

  // ------------------- Action-card visibility by role --------------------
  const canLocation  = canCreateLocation();
  const canWarehouse = canCreateWarehouse();
  const canPartition = canCreateRoom();
  const canProduct   = isAdmin();                    // matches /products gating
  const canUser      = canCreateUser("worker") || canCreateUser("admin");
  const canTag       = canManageTags() || (isWorker()); // workers can create in their rooms
  const anyAction    = canLocation || canWarehouse || canPartition || canProduct || canUser || canTag;

  // ------------------- Dropdown option lists -----------------------------
  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: String(l._id), label: l.name })),
    [locations]
  );
  // Managers who only own certain locations still only see warehouses within
  // those locations; the Dashboard already fetches scoped data for them.
  const warehouseOptions = useMemo(() => {
    const filtered = isLocationManager()
      ? warehouses
      : warehouses;
    return filtered.map((w) => ({
      value: String(w._id),
      label: w.name + (w.locationName ? ` · ${w.locationName}` : ""),
    }));
  }, [warehouses, isLocationManager]);

  // ------------------- Create handlers -----------------------------------
  const handleCreateLocation = async () => {
    const f = forms.location;
    if (!f.name.trim()) return showToast("Location name is required", 3000, true, "danger");
    setSaving(true);
    try {
      await api.createLocation({ name: f.name.trim(), address: f.address.trim() });
      showToast("Location created", 2500, true, "success");
      resetAndClose();
      onCreated?.();
    } catch (err) {
      showToast(err.message || "Failed to create location", 3000, true, "danger");
    } finally { setSaving(false); }
  };

  const handleCreateWarehouse = async () => {
    const f = forms.warehouse;
    if (!f.name.trim())  return showToast("Warehouse name is required", 3000, true, "danger");
    if (!f.locationId)   return showToast("Pick a location", 3000, true, "danger");
    setSaving(true);
    try {
      await api.createWarehouse({ name: f.name.trim(), locationId: f.locationId });
      showToast("Warehouse created", 2500, true, "success");
      resetAndClose();
      onCreated?.();
    } catch (err) {
      showToast(err.message || "Failed to create warehouse", 3000, true, "danger");
    } finally { setSaving(false); }
  };

  const handleCreatePartition = async () => {
    const f = forms.partition;
    if (!f.roomTitle.trim()) return showToast("Partition title is required", 3000, true, "danger");
    if (!f.warehouseId)      return showToast("Pick a warehouse", 3000, true, "danger");
    setSaving(true);
    try {
      await api.createRoom({ roomTitle: f.roomTitle.trim(), warehouseId: f.warehouseId });
      showToast("Partition created", 2500, true, "success");
      resetAndClose();
      onCreated?.();
    } catch (err) {
      showToast(err.message || "Failed to create partition", 3000, true, "danger");
    } finally { setSaving(false); }
  };

  const handleCreateProduct = async () => {
    const f = forms.product;
    const epc = (f.epc || "").trim().toUpperCase();
    const priceNum = Number(f.price);
    if (!f.name.trim())           return showToast("Product name is required", 3000, true, "danger");
    if (!epc || epc.length < 4)   return showToast("EPC must be at least 4 hex chars", 3000, true, "danger");
    if (!/^[0-9A-F]+$/.test(epc)) return showToast("EPC must be hexadecimal", 3000, true, "danger");
    if (!Number.isFinite(priceNum) || priceNum < 0) return showToast("Price must be a non-negative number", 3000, true, "danger");
    setSaving(true);
    try {
      await api.createProduct({ name: f.name.trim(), epc, price: priceNum });
      showToast("Product created", 2500, true, "success");
      resetAndClose();
      onCreated?.();
    } catch (err) {
      showToast(err.message || "Failed to create product", 3000, true, "danger");
    } finally { setSaving(false); }
  };

  const handleCreateUser = async () => {
    const f = forms.user;
    if (!f.username.trim())  return showToast("Username is required", 3000, true, "danger");
    if (!f.password || f.password.length < 6) return showToast("Password must be at least 6 characters", 3000, true, "danger");
    if (!canCreateUser(f.role)) return showToast(`You can't create ${f.role}s`, 3000, true, "danger");
    setSaving(true);
    try {
      await api.register({ username: f.username.trim(), password: f.password, role: f.role });
      showToast(`User "${f.username}" created`, 2500, true, "success");
      resetAndClose();
      onCreated?.();
    } catch (err) {
      showToast(err.message || "Failed to create user", 3000, true, "danger");
    } finally { setSaving(false); }
  };

  if (!anyAction) return null; // Nothing to show; skip the whole panel.

  // ------------------- Render --------------------------------------------
  return (
    <>
      <div className="quick-add-section">
        <div className="quick-add-header">
          <i className="fa fa-bolt quick-add-header-icon" aria-hidden />
          <div>
            <h3 className="quick-add-title">Quick Actions</h3>
            <p className="quick-add-subtitle">Create common records without leaving the dashboard.</p>
          </div>
        </div>
        <div className="quick-action-grid">
          {canLocation && (
            <QuickAddCard icon="fa-map-marker" label="Location" description="A physical site / campus"
              accent="#10B981" onClick={() => setOpenModal("location")} />
          )}
          {canWarehouse && (
            <QuickAddCard icon="fa-building" label="Warehouse" description="A building inside a location"
              accent="#F59E0B" onClick={() => setOpenModal("warehouse")} />
          )}
          {canPartition && (
            <QuickAddCard icon="fa-cube" label="Partition" description="A room / zone inside a warehouse"
              accent="#EC4899" onClick={() => setOpenModal("partition")} />
          )}
          {canProduct && (
            <QuickAddCard icon="fa-cubes" label="Product" description="A new catalog entry"
              accent="#8B5CF6" onClick={() => setOpenModal("product")} />
          )}
          {canTag && (
            <QuickAddCard icon="fa-tag" label="Tag" description="Register a physical RFID tag"
              accent="#3B82F6" onClick={() => onOpenTagModal?.()} />
          )}
          {canUser && (
            <QuickAddCard icon="fa-user-plus" label="User" description="Invite a new team member"
              accent="#6366F1" onClick={() => setOpenModal("user")} />
          )}
        </div>
      </div>

      {/* ---------------- Modals -------------------------------------- */}
      {openModal === "location" && (
        <QuickModal
          icon="fa-map-marker"
          iconBg="#10B98122"
          iconColor="#10B981"
          title="New Location"
          subtitle="A site, campus, or building cluster."
          onClose={resetAndClose}
          onSubmit={handleCreateLocation}
          saving={saving}
          submitLabel="Create Location"
        >
          <FormField label="Name" required>
            <TextField value={forms.location.name}
              onChange={(e) => updateForm("location", { name: e.target.value })}
              placeholder="e.g. Cairo HQ" variant="outlined" />
          </FormField>
          <FormField label="Address">
            <TextField value={forms.location.address}
              onChange={(e) => updateForm("location", { address: e.target.value })}
              placeholder="Optional" variant="outlined" />
          </FormField>
        </QuickModal>
      )}

      {openModal === "warehouse" && (
        <QuickModal
          icon="fa-building"
          iconBg="#F59E0B22"
          iconColor="#F59E0B"
          title="New Warehouse"
          subtitle="A building inside a location."
          onClose={resetAndClose}
          onSubmit={handleCreateWarehouse}
          saving={saving}
          submitLabel="Create Warehouse"
        >
          <FormField label="Name" required>
            <TextField value={forms.warehouse.name}
              onChange={(e) => updateForm("warehouse", { name: e.target.value })}
              placeholder="e.g. Main WH" variant="outlined" />
          </FormField>
          <FormField label="Location" required>
            <Dropdown options={locationOptions}
              value={forms.warehouse.locationId}
              onChange={(val) => updateForm("warehouse", { locationId: val })}
              placeholder={locationOptions.length ? "Select a location" : "No locations available yet"}
              variant="outlined" />
          </FormField>
        </QuickModal>
      )}

      {openModal === "partition" && (
        <QuickModal
          icon="fa-cube"
          iconBg="#EC489922"
          iconColor="#EC4899"
          title="New Partition"
          subtitle="A room or zone inside a warehouse."
          onClose={resetAndClose}
          onSubmit={handleCreatePartition}
          saving={saving}
          submitLabel="Create Partition"
        >
          <FormField label="Title" required>
            <TextField value={forms.partition.roomTitle}
              onChange={(e) => updateForm("partition", { roomTitle: e.target.value })}
              placeholder="e.g. Aisle A" variant="outlined" />
          </FormField>
          <FormField label="Warehouse" required>
            <Dropdown options={warehouseOptions}
              value={forms.partition.warehouseId}
              onChange={(val) => updateForm("partition", { warehouseId: val })}
              placeholder={warehouseOptions.length ? "Select a warehouse" : "No warehouses available yet"}
              variant="outlined" />
          </FormField>
        </QuickModal>
      )}

      {openModal === "product" && (
        <QuickModal
          icon="fa-cubes"
          iconBg="#8B5CF622"
          iconColor="#8B5CF6"
          title="New Product"
          subtitle="A catalog entry linked by EPC. Add an image later on the Products page."
          onClose={resetAndClose}
          onSubmit={handleCreateProduct}
          saving={saving}
          submitLabel="Create Product"
        >
          <FormField label="Name" required>
            <TextField value={forms.product.name}
              onChange={(e) => updateForm("product", { name: e.target.value })}
              placeholder="e.g. Surgical Glove, Large" variant="outlined" />
          </FormField>
          <FormField label="EPC" required hint="4–24 hex characters (0-9, A-F)">
            <TextField value={forms.product.epc}
              onChange={(e) => updateForm("product", {
                epc: e.target.value.toUpperCase().replace(/[^0-9A-F]/g, "").slice(0, 24),
              })}
              placeholder="A1B2C3D4" variant="outlined" />
          </FormField>
          <FormField label="Price" required>
            <TextField type="number" value={forms.product.price}
              onChange={(e) => updateForm("product", { price: e.target.value })}
              placeholder="0.00" variant="outlined" />
          </FormField>
        </QuickModal>
      )}

      {openModal === "user" && (
        <QuickModal
          icon="fa-user-plus"
          iconBg="#6366F122"
          iconColor="#6366F1"
          title="New User"
          subtitle={isManager() && !isAdmin() ? "Managers can only create workers and viewers." : "Assign a role for access-scoping."}
          onClose={resetAndClose}
          onSubmit={handleCreateUser}
          saving={saving}
          submitLabel="Create User"
        >
          <FormField label="Username" required>
            <TextField value={forms.user.username}
              onChange={(e) => updateForm("user", { username: e.target.value })}
              placeholder="jane.doe" variant="outlined" />
          </FormField>
          <FormField label="Password" required hint="Min 6 characters">
            <TextField type="password" value={forms.user.password}
              onChange={(e) => updateForm("user", { password: e.target.value })}
              placeholder="••••••••" variant="outlined" />
          </FormField>
          <FormField label="Role" required>
            <Dropdown
              options={USER_ROLE_OPTIONS.filter((opt) => canCreateUser(opt.value))}
              value={forms.user.role}
              onChange={(val) => updateForm("user", { role: val })}
              variant="outlined"
            />
          </FormField>
        </QuickModal>
      )}
    </>
  );
};

// Generic modal shell matching the existing Dashboard.css modal classes.
const QuickModal = ({ icon, iconBg, iconColor, title, subtitle, onClose, onSubmit, saving, submitLabel, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
      <div className="modal-header-wrapper">
        <div className="modal-icon-circle" style={{ backgroundColor: iconBg, color: iconColor }}>
          <i className={`fa ${icon}`}></i>
        </div>
        <div className="modal-title-section">
          <span className="modal-title">{title}</span>
          {subtitle && <span className="modal-subtitle">{subtitle}</span>}
        </div>
        <div className="modal-close-btn" onClick={onClose}>
          <i className="fa fa-times"></i>
        </div>
      </div>
      <div className="modal-body-wrapper" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {children}
      </div>
      <div className="modal-footer-wrapper">
        <Button value="Cancel" iconLeft="fa-times" variant="outline" onClick={onClose} disabled={saving} />
        <Button value={saving ? "Creating..." : submitLabel}
          iconLeft={saving ? "fa-spinner fa-spin" : "fa-plus"}
          variant="primary"
          onClick={onSubmit}
          disabled={saving} />
      </div>
    </div>
  </div>
);

const FormField = ({ label, required, hint, children }) => (
  <div className="form-field">
    <div className="form-label-wrapper">
      <span className="form-label">{label}</span>
      {required && <span className="form-label-required">*</span>}
      {hint && <span className="form-label-hint">{hint}</span>}
    </div>
    {children}
  </div>
);

export default QuickAdd;
